import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Spin, Table, Tag, Empty, Button, message } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';
import { ACL } from 'components/common';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import { getApprovalDetailsV2 } from 'store/approvalSliceV2';
import VerificationDecisionModals from './VerificationDecisionModals';
import { VERIFICATION_KINDS, VERIFICATION_STATUS_COLORS } from './verificationConfig';

const PageContainer = styled(Box)`
	max-width: 1200px;
	margin: 0 auto;
	padding: 24px;
`;

const Header = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	margin-bottom: 24px;
`;

const BackLink = styled(Flex)`
	align-items: center;
	gap: 12px;
	cursor: pointer;
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const Card = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 20px;
`;

const SectionTitle = styled(Text)`
	display: block;
	font-size: 16px;
	font-weight: 600;
	color: #333;
	margin-bottom: 16px;
`;

const InfoGrid = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
`;

const Label = styled(Text)`
	display: block;
	font-size: 12px;
	color: #666;
	margin-bottom: 6px;
`;

const Value = styled(Text)`
	display: block;
	font-size: 15px;
	font-weight: 600;
	color: #000;
	word-break: break-word;
`;

const Notice = styled(Box)`
	border-radius: 6px;
	padding: 12px 16px;
	margin-bottom: 20px;
	background: ${(props) => (props.$rejected ? '#fff1f0' : '#f6ffed')};
	border: 1px solid ${(props) => (props.$rejected ? '#ffa39e' : '#b7eb8f')};
`;

const ActionBar = styled(Flex)`
	gap: 12px;
`;

const CenterBox = styled(Box)`
	display: flex;
	justify-content: center;
	padding: 80px 20px;
`;

// Drop the upload prefix (a UUID) so reviewers see the original file name.
const fileNameFromKey = (key = '') =>
	String(key)
		.replace(/^\/+/, '')
		.replace(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i, '');

const formatDate = (value) => (value ? moment(value).format('DD-MM-YYYY') : '-');
const formatDateTime = (value) => (value ? moment(value).format('DD-MM-YYYY hh:mm A') : '-');

export default function VerificationDetailsPageV2({ kind }) {
	const config = VERIFICATION_KINDS[kind];
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const [preview, setPreview] = useState(null);
	const [mode, setMode] = useState(null);
	const [actionLoading, setActionLoading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const result = await dispatch(getApprovalDetailsV2({ approval_id: id }));
			if (!result?.approval || result.approval.approval_type !== config.approvalType) {
				message.error('Approval not found');
				navigate(config.listPath);
				return;
			}
			setData(result);
		} catch (error) {
			message.error('Failed to fetch approval details');
			navigate(config.listPath);
		} finally {
			setLoading(false);
		}
	}, [dispatch, id, config, navigate]);

	useEffect(() => {
		load();
	}, [load]);

	const handleConfirm = async (text) => {
		setActionLoading(true);
		const action =
			mode === 'approve'
				? config.approve({ approval_id: id, comments: text })
				: config.reject({ approval_id: id, rejection_reason: text });
		const result = await dispatch(action);
		setActionLoading(false);
		if (result.success) {
			setMode(null);
			navigate(config.listPath);
		}
	};

	if (loading) {
		return (
			<CenterBox>
				<Spin size="large" />
			</CenterBox>
		);
	}

	const { approval, entity_details: details = {} } = data;
	const documents = details.documents || [];
	const isPending = approval.status === 'pending';

	const documentColumns = [
		{ title: 'Document', dataIndex: 'label', key: 'label' },
		{ title: 'Number', dataIndex: 'number', key: 'number', render: (v) => v || '-' },
		{ title: 'Valid Till', dataIndex: 'valid_till', key: 'valid_till', render: formatDate },
		{
			title: 'File',
			key: 'file',
			render: (_, doc) => fileNameFromKey(doc.document_key),
		},
		{
			title: 'View',
			key: 'view',
			render: (_, doc) => (
				<Button
					type="link"
					icon={<EyeOutlined />}
					onClick={() =>
						setPreview({
							key: String(doc.document_key).replace(/^\/+/, ''),
							name: `${doc.label} - ${fileNameFromKey(doc.document_key)}`,
							entity: doc.entity,
						})
					}
				>
					View
				</Button>
			),
		},
	];

	return (
		<PageContainer>
			<Header>
				<BackLink onClick={() => navigate(config.listPath)}>
					<ArrowLeftOutlined />
					{config.noun} Approval
					<Tag color={VERIFICATION_STATUS_COLORS[approval.status] || 'default'}>
						{approval.status}
					</Tag>
				</BackLink>
				{isPending && (
					<ActionBar>
						<ACL action="update" module={config.aclModule}>
							<Button
								type="primary"
								icon={<CheckOutlined />}
								onClick={() => setMode('approve')}
							>
								Approve
							</Button>
						</ACL>
						<ACL action="update" module={config.aclModule}>
							<Button
								danger
								icon={<CloseOutlined />}
								onClick={() => setMode('reject')}
							>
								Reject
							</Button>
						</ACL>
					</ActionBar>
				)}
			</Header>

			{approval.status === 'rejected' && (
				<Notice $rejected>
					<Text fontWeight={600}>Rejected by {approval.rejected_by_name || '-'}</Text>
					<Text display="block">Reason: {approval.rejection_reason || '-'}</Text>
				</Notice>
			)}
			{approval.status === 'approved' && (
				<Notice>
					<Text fontWeight={600}>Approved by {approval.approver_name || '-'}</Text>
					{approval.comments && (
						<Text display="block">Comments: {approval.comments}</Text>
					)}
				</Notice>
			)}

			<Card>
				<SectionTitle>{config.noun} Details</SectionTitle>
				<InfoGrid>
					{config.fields(details).map((field) => (
						<Box key={field.key}>
							<Label>{field.label}</Label>
							<Value>{field.value}</Value>
						</Box>
					))}
					<Box>
						<Label>Submitted By</Label>
						<Value>{approval.requested_by_name || '-'}</Value>
					</Box>
					<Box>
						<Label>Submitted On</Label>
						<Value>{formatDateTime(approval.requested_at)}</Value>
					</Box>
				</InfoGrid>
			</Card>

			<Card>
				<SectionTitle>Documents ({documents.length})</SectionTitle>
				{documents.length === 0 ? (
					<Empty description="No documents uploaded" />
				) : (
					<Table
						rowKey="type"
						columns={documentColumns}
						dataSource={documents}
						pagination={false}
					/>
				)}
			</Card>

			{preview && (
				<FilePreviewModal
					visible
					fileKey={preview.key}
					fileName={preview.name}
					entity={preview.entity}
					onClose={() => setPreview(null)}
				/>
			)}

			<VerificationDecisionModals
				mode={mode}
				noun={config.noun}
				loading={actionLoading}
				onConfirm={handleConfirm}
				onClose={() => setMode(null)}
			/>
		</PageContainer>
	);
}

VerificationDetailsPageV2.propTypes = {
	kind: PropTypes.oneOf(['driver', 'vehicle']).isRequired,
};
